import { NgIf } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OrderSummaryRow } from '@common/order-summary-row';
import { EmbeddedOrderSummaryComponent } from '@components/embedded-order-summary/embedded-order-summary.component';
import { Treatment } from '@enums/treatment';
import { environment } from '@environments/environment';
import { ConfigService, GiftCardPayment, Payment, PaymentTypes } from '@medology/ngx-payment';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-consultation-order-summary',
  templateUrl: './consultation-order-summary.component.html',
  styleUrls: ['./consultation-order-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [EmbeddedOrderSummaryComponent, NgIf, FormsModule, ReactiveFormsModule],
})
export class ConsultationOrderSummaryComponent implements OnInit, OnDestroy {
  @Input() public consultationRequestPaymentForm: FormGroup;

  @Output() public fullDiscountApplied = new EventEmitter<void>();

  public orderSummaryRows: OrderSummaryRow[] = [];

  public consultationRequestService = inject(ConsultationRequestService);

  private subscriptions: Subscription[] = [];
  private _isFreeSelected: boolean = false;
  private _payments: Payment[] = [];

  private configService = inject(ConfigService);

  /**
   * The currently selected payments, used to derive the applied gift card amount.
   */
  public get payments(): Payment[] {
    return this._payments;
  }

  /**
   * Gets the total to display in the order summary after applying the gift card amount.
   */
  public get totalInCents(): number {
    return this.consultationRequestService.getTotal() - this.giftCardAmountInCents;
  }

  /**
   * Whether the free payment option is enabled.
   */
  public get isFreeEnabled(): boolean {
    return this.configService.paymentConfig().enabled?.includes(PaymentTypes.Free);
  }

  /**
   * Whether the free payment option is currently selected.
   */
  public get isFreeSelected(): boolean {
    return this._isFreeSelected;
  }

  /**
   * Gets the applied gift card amount in cents, capped at the consultation total.
   */
  private get giftCardAmountInCents(): number {
    const giftCard = this._payments.find((payment): payment is GiftCardPayment => payment instanceof GiftCardPayment);
    if (!giftCard) {
      return 0;
    }

    return Math.min(Math.round(giftCard.balance * 100), this.consultationRequestService.getTotal());
  }

  /**
   * Gets the site-specific gift card line item label (e.g. "STDcheck.com Gift Card").
   */
  private get giftCardItemName(): string {
    return `${environment.domain} Gift Card`;
  }

  /**
   * Get the form discount control.
   */
  private get discountControl(): AbstractControl {
    return this.consultationRequestPaymentForm.get('discount');
  }

  /**
   * Sets the currently selected payments and refreshes the order summary rows so the gift card row stays in sync.
   */
  @Input() public set payments(value: Payment[]) {
    this._payments = value ?? [];
    this.setOrderSummaryRows();
  }

  /**
   * Sets whether the free payment option is selected.
   *
   * Resets the discount to 0 and refreshes the order summary rows whenever the value changes.
   */
  @Input() public set isFreeSelected(value: boolean) {
    if (value === this._isFreeSelected) {
      return;
    }

    this._isFreeSelected = value;
    this.syncDiscountAndSummary();
  }

  /**
   * Initialize form listeners.
   */
  public ngOnInit(): void {
    this.subscriptions.push(this.listenForDiscountChanges(), this.listenForTreatmentChanges());

    this.setOrderSummaryRows();
  }

  /**
   * Unsubscribe all current subscriptions.
   */
  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Listen for discount amount changes to set the payment method to free
   * if the discount exceeds the subtotal of the order.
   *
   * @returns a Subscription to changes in the form's discount value
   */
  private listenForDiscountChanges(): Subscription {
    return this.discountControl.valueChanges.subscribe((value) => {
      const discountAmount = (value ?? 0) * 100;

      if (discountAmount >= this.consultationRequestService.getSubtotal()) {
        this.fullDiscountApplied.emit();
      }

      this.setOrderSummaryRows();
    });
  }

  /**
   * Listen for treatment changes to reset the discount amount.
   *
   * @returns a Subscription to changes in the form's treatment value
   */
  private listenForTreatmentChanges(): Subscription {
    return this.consultationRequestService.treatmentControl.valueChanges.subscribe(() => this.syncDiscountAndSummary());
  }

  /**
   * Sets the order summary rows for the consultation request.
   */
  private setOrderSummaryRows(): void {
    this.orderSummaryRows = [
      {
        item: 'Clinical Review',
        amountInCents: ConsultationRequestService.cost,
        shouldDisplayRow: this.consultationRequestService.treatmentControl.value !== Treatment.Partner,
        dataQa: 'your-consultation-row',
      },
      {
        item: 'Clinical Review for your Partner',
        amountInCents: ConsultationRequestService.cost,
        shouldDisplayRow: this.consultationRequestService.treatmentControl.value !== Treatment.Self,
        dataQa: 'partner-consultation-row',
      },
      {
        item: 'Clinical Review Discount',
        amountInCents: -this.consultationRequestService.getDiscount(),
        shouldDisplayRow: Boolean(this.consultationRequestService.getDiscount()),
        dataQa: 'consultation-discount-row',
      },
      {
        item: this.giftCardItemName,
        amountInCents: -this.giftCardAmountInCents,
        shouldDisplayRow: this.giftCardAmountInCents > 0,
        dataQa: 'gift-card-row',
      },
    ];
  }

  /**
   * Patches the discount control based on whether the free payment option is selected, then refreshes the order summary
   * rows.
   */
  private syncDiscountAndSummary(): void {
    this.discountControl.patchValue(this.isFreeSelected ? this.consultationRequestService.getSubtotal() / 100 : 0);
    this.setOrderSummaryRows();
  }
}
